import { ArrowUp } from "lucide-react";
import { Fragment, useEffect } from "react";
import { useApp } from "../state/store.ts";
import { checkForUpdates, closeUpdateDialog, installNow, installOnNextLaunch, openReleasesPage, useUpdater } from "../lib/updater.ts";

const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

/** Release notes: "- " / "* " lines become a list, "#" lines a small heading, **bold** stays bold. */
function Notes({ text }: { text: string }) {
  const blocks: ({ kind: "p" | "h"; text: string } | { kind: "ul"; items: string[] })[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const item = line.match(/^[-*•]\s+(.*)$/);
    const head = line.match(/^#{1,6}\s+(.*)$/);
    if (item) {
      const last = blocks[blocks.length - 1];
      if (last?.kind === "ul") last.items.push(item[1]);
      else blocks.push({ kind: "ul", items: [item[1]] });
    } else blocks.push({ kind: head ? "h" : "p", text: head ? head[1] : line });
  }
  const inline = (s: string) =>
    s.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? <b key={i}>{part.slice(2, -2)}</b> : <Fragment key={i}>{part.replace(/`/g, "")}</Fragment>,
    );
  return (
    <div className="update-notes small">
      {blocks.map((b, i) =>
        b.kind === "ul" ? <ul key={i}>{b.items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ul>
        : b.kind === "h" ? <h4 key={i}>{inline(b.text)}</h4>
        : <p key={i}>{inline(b.text)}</p>,
      )}
    </div>
  );
}

/** "New update available" (desktop app) — lib/updater.ts. */
export function UpdateDialog() {
  const { open, phase, info, current, progress, bytes, error, queued, failed } = useUpdater();
  const busy = phase === "downloading" || phase === "installing";

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) { e.stopPropagation(); closeUpdateDialog(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, busy]);

  if (!open) return null;
  const later = () => {
    const v = installOnNextLaunch();
    if (v) useApp.getState().set({ notice: { text: `MC Planner ${v} will be installed the next time you open the app.` } });
  };

  return (
    <div className="modal-back update-back" onClick={busy ? undefined : closeUpdateDialog}>
      <div className="modal update-dialog" role="dialog" aria-modal="true" aria-labelledby="update-title" onClick={(e) => e.stopPropagation()}>
        {phase === "checking" && (
          <>
            <h3 id="update-title">Checking for updates…</h3>
            <div className="bar indeterminate"><i /></div>
          </>
        )}

        {phase === "uptodate" && (
          <>
            <h3 id="update-title">You're up to date</h3>
            <p className="small">MC Planner {current ?? ""} is the newest version.</p>
            <div className="row update-actions"><button className="primary" autoFocus onClick={closeUpdateDialog}>OK</button></div>
          </>
        )}

        {phase === "available" && info && (
          <>
            <div className="update-head">
              <span className="update-badge" aria-hidden="true"><ArrowUp size={22} strokeWidth={2.5} /></span>
              <div>
                <h3 id="update-title">New update available</h3>
                <p className="small muted">MC Planner <b className="update-version">{info.version}</b> is ready · you have {info.current}</p>
              </div>
            </div>
            {info.notes && <Notes text={info.notes} />}
            <div className="row update-actions">
              <button className="primary" autoFocus onClick={() => void installNow()}>Install now</button>
              <button onClick={later}>Install on next launch</button>
              <button className="ghost" onClick={closeUpdateDialog}>Cancel</button>
            </div>
            <p className="tiny muted">Install now restarts MC Planner. Your plans are saved as you go, so nothing is lost.</p>
          </>
        )}

        {busy && (
          <>
            <h3 id="update-title">{phase === "installing" ? "Installing…" : `Updating to ${info?.version ?? "the new version"}…`}</h3>
            {queued && phase === "downloading" && <p className="small muted">You chose to install this update when MC Planner starts.</p>}
            <div className={`bar${progress === null ? " indeterminate" : ""}`}><i style={progress === null ? undefined : { width: `${Math.round(progress * 100)}%` }} /></div>
            <p className="tiny muted">
              {phase === "installing"
                ? "MC Planner closes and opens again by itself in a moment."
                : bytes[1] ? `${mb(bytes[0])} of ${mb(bytes[1])}` : bytes[0] ? mb(bytes[0]) : "Starting the download…"}
            </p>
          </>
        )}

        {phase === "error" && (
          <>
            <h3 id="update-title">{failed === "install" ? "The update didn't work" : "Couldn't check for updates"}</h3>
            <p className="small">{error}</p>
            {failed === "install" && info && <p className="small muted">You can download MC Planner {info.version} and install it over this version — your plans stay.</p>}
            <div className="row update-actions">
              <button className="primary" autoFocus onClick={() => void checkForUpdates({ manual: true, install: failed === "install" })}>Try again</button>
              {failed === "install" && <button onClick={openReleasesPage}>Download it</button>}
              <button className="ghost" onClick={closeUpdateDialog}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Help overlay line (desktop): "MC Planner 0.2.0 · Check for updates". */
export function AppVersion() {
  const current = useUpdater((s) => s.current);
  const phase = useUpdater((s) => s.phase);
  if (!current) return null;
  return (
    <p className="app-version tiny muted">
      MC Planner {current} ·{" "}
      <button className="link" disabled={phase === "checking" || phase === "downloading" || phase === "installing"} onClick={() => void checkForUpdates({ manual: true })}>
        Check for updates
      </button>
    </p>
  );
}
